
import {
  ellipsisString,
  formatDateNumber,
  formatPrice,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/FormatPhoneNumber";
import { Range } from "@/app/ui/components/date-range-picker/type";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { PrintReceiptController } from "@/app/ui/controllers/PrintReceiptController";
import { ListInvoiceApiRequest } from "@/data/payload/api/PrintReceiptRequest";
import { debounce } from "lodash";
import OverlayPanel from "primevue/overlaypanel";
import { Options, Vue } from "vue-class-component";
import TableTagihan from "./components/TableTagihan.vue";
import ModalUpdateInvoice from "./components/modal-update-invoice.vue";
import {
  InvoiceData,
  PrintReceiptListInvoice,
  RecipientTag,
  ListInvoiceItem,
  Invoice,
  SttInvoice,
  InvoiceDataInterface
} from "@/domain/entities/PrintReceipt";
import ModalSuccessSendInvoice from "./components/ModalSuccessSendInvoice.vue";
import ModalSendInvoice from "./components/ModalSendInvoice.vue";
import InvoiceRecipientTagPicker from "./components/InvoiceRecipientTagPicker.vue";
import InvoicePdfA4 from "./components/invoice/InvoicePdfA4.vue";
import InvoicePdfStruk from "./components/invoice/InvoicePdfStruk.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import FormInvoiceDetail from "./components/FormInvoiceDetail.vue";
import { ActivityRequest } from "@/data/payload/api/PrintReceiptApiRequest";
import formatDate from '@/app/infrastructures/misc/common-library/v2/FormatDate';

@Options({
  components: {
    ModalSendInvoice,
    ModalSuccessSendInvoice,
    ModalUpdateInvoice,
    TableTagihan,
    OverlayPanel,
    InvoiceRecipientTagPicker,
    InvoicePdfA4,
    InvoicePdfStruk,
    FormInvoiceDetail
  }
})
class PrintReceiptV2 extends Vue {
  isLoading = false;
  statusSelected: any = "";
  errorCause = "";
  params = new ListInvoiceApiRequest();
  invoicesData = new PrintReceiptListInvoice();
  recipientTags: RecipientTag[] = [];

  showModalUpdateInvoice = false;
  showSendModal = false;
  showSendInvoiceSuccess = false;
  currentData: Partial<InvoiceData> = {};
  actionOPRefs: any[] = [];

  invoiceData = new Invoice();

  handleSuccess(response: any) {
    // handle after success update invoice
  }

  get accountCurrency(): "IDR" | "MYR" {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }

  get statusOptionList() {
    return [
      { name: this.$t("tagihan.status.all"), value: "" },
      { name: this.$t("tagihan.status.unpaid"), value: "unpaid" },
      {
        name: this.$t("tagihan.status.partiallyPaid"),
        value: "partially_paid"
      },
      { name: this.$t("tagihan.status.paid"), value: "paid" }
    ];
  }

  get column() {
    const currency = this.accountCurrency;
    return [
      {
        name: this.$t("tagihan.table.no"),
        field: "no",
        style: { width: "50px" }
      },
      {
        name: this.$t("tagihan.table.invoiceId"),
        field: "invoiceNo",
        style: { width: "250px" }
      },
      {
        name: this.$t("tagihan.table.billedTo"),
        field: "billedToName",
        slotName: "billedTo",
        style: { width: "250px" }
      },
      {
        name: this.$t("tagihan.table.totalAmount"),
        field: "totalAmount",
        slotName: "totalAmount",
        style: { width: "188.5px" },
        render: (item: any) => ({
          text: formatPrice(item.totalAmount, "", currency),
          class: "font-semibold text-sm text-gray-900"
        })
      },
      {
        name: this.$t("tagihan.table.totalPaid"),
        field: "totalPaid",
        slotName: "totalPaid",
        style: { width: "188.5px" },
        render: (item: any) => ({
          text: formatPrice(item.totalPaid, "", currency),
          class: "font-semibold text-sm text-gray-900"
        })
      },
      {
        name: this.$t("tagihan.table.status"),
        field: "status",
        slotName: "status",
        style: { width: "188.5px" }
      },
      {
        name: this.$t("tagihan.table.activities"),
        field: "isInvoiceSent",
        slotName: "activities",
        style: { width: "188.5px" }
      },
      {
        name: this.$t("tagihan.table.createdAt"),
        field: "createdAt",
        slotName: "createdAt",
        style: { width: "280px" }
      },
      {
        name: this.$t("tagihan.table.updatedAt"),
        field: "updatedBy",
        slotName: "updatedAt",
        style: { width: "280px" }
      },
      {
        name: this.$t("tagihan.table.action"),
        slotName: "action",
        style: { width: "84px" },
        isAction: true
      }
    ];
  }

  formatDateWithTz(value: string, fmt: string) {
    return formatDate(value, fmt);
  }

  formatPhoneForWa(phone: string) {
    const formatted = formatPhoneNumber(phone);
    return formatted.replace(/\D/g, "");
  }

  getStatusBadge(status?: string) {
    switch (status) {
      case "unpaid":
        return {
          class: "bg-red-100 text-red-700",
          label: this.$t("tagihan.status.unpaid"),
          icon: require("@/app/ui/assets/svg/ic_circle_red.svg")
        };

      case "partially_paid":
        return {
          class: "bg-yellow-100 text-yellow-700",
          label: this.$t("tagihan.status.partiallyPaid"),
          icon: require("@/app/ui/assets/svg/ic_circle_yellow.svg")
        };

      case "paid":
        return {
          class: "bg-green-100 text-green-700",
          label: this.$t("tagihan.status.paid"),
          icon: require("@/app/ui/assets/svg/ic_circle_green.svg")
        };

      default:
        return {
          class: "bg-gray-100 text-gray-600",
          label: status ?? "-"
        };
    }
  }

  getActivityItem(isActive: boolean) {
    return {
      icon: isActive
        ? require("@/app/ui/assets/svg/ics_f_check_circle_green.svg")
        : require("@/app/ui/assets/svg/ics_f_check_circle_gray.svg"),
      textClass: isActive ? "text-gray-800" : "text-gray-400"
    };
  }

  async fetchListInvoices() {
    this.isLoading = true;
    const params = new ListInvoiceApiRequest({
      dateFrom: formatDateNumber(this.params.dateFrom),
      dateTo: formatDateNumber(this.params.dateTo),
      status: this.params.status,
      search: this.params.search,
      page: this.params.page
    });

    try {
      const resp = await PrintReceiptController.getListInvoices(params);
      if (resp.data) {
        this.invoicesData = resp;
      }
    } catch (error) {
      const err = error as any;
      this.errorCause = err.response ? "server" : "internet";
      console.error("error fetch list error: ", error);
    } finally {
      this.isLoading = false;
    }
  }

  onSearch = debounce(async (value) => {
    this.params.page = 1;
    this.params.search = value;
    await this.fetchListInvoices();
  }, 250);

  onClear() {
    this.params.search = "";
    this.params.page = 1;
    this.fetchListInvoices();
  }

  setDateRange(value: Array<any>) {
    this.params.dateFrom = value[0];
    this.params.dateTo = value[1];

    if (value[0] && value[1]) {
      this.params.page = 1;
      this.fetchListInvoices();
    }
  }

  onSelectStatus() {
    this.params.status = this.statusSelected.value;
    this.params.page = 1;
    this.fetchListInvoices();
  }

  toggleAction(event: any, index: number) {
    this.actionOPRefs.forEach((op, i) => {
      if (i !== index) op?.hide();
    });
    this.actionOPRefs[index]?.toggle(event);
  }

  ellipsisString(value: string, long: number) {
    return ellipsisString(value, long);
  }

  onSendSuccess() {
    this.showSendModal = false;
  }

  invoiceDataProps = new InvoiceDataInterface();
  async onShowModalSendInvoice(
    data: ListInvoiceItem,
    event: any,
    index: number
  ) {
    this.toggleAction(event, index);
    try {
      MainAppController.showLoading();
      this.invoiceData = await PrintReceiptController.getDetailInvoice(data.id);
      this.invoiceDataProps = new InvoiceDataInterface({
        id: data.id,
        invoiceNo: this.invoiceData.header.invoiceNo
      });
      this.invoiceData.header.invoiceId = data.id;

      PrintReceiptController.setInvoiceDetailData(this.invoiceData);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("tagihan.errorGetDetail") as string,
          () => this.onShowModalSendInvoice(data, event, index)
        )
      );
    } finally {
      MainAppController.closeLoading();
    }

    this.currentData.id = data.id;
    this.showSendModal = true;
  }

  async onShowModalUpdateInvoice(
    data: ListInvoiceItem,
    event: any,
    index: number
  ) {
    this.toggleAction(event, index);
    this.currentData.id = data.id;
    this.showModalUpdateInvoice = true;
  }

  goToCreateReceipt() {
    this.$router.push("/shipment/receipt-v2/create");
  }

  mounted() {
    this.fetchListInvoices();
  }

  async onDownload(
    event: any,
    index: number,
    type: "pdf-a4" | "pdf-struk" | "png",
    id = 0
  ) {
    try {
      MainAppController.showLoading();
      this.invoiceData = await PrintReceiptController.getDetailInvoice(id);
      await this.$nextTick();

      if (type === "pdf-a4") {
        await (this.$refs as any).invoice.downloadPdfA4();
      }
      if (type === "png") {
        await (this.$refs as any).invoice.downloadPngA4();
      }
      if (type === "pdf-struk") {
        await (this.$refs as any).struk.downloadPdfStruk();
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("tagihan.errorGetDetail") as string,
          () => this.onDownload(event, index, type, id)
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async download(
    event: any,
    index: number,
    type: "pdf-a4" | "pdf-struk" | "png",
    id = 0
  ) {
    this.toggleAction(event, index);
    const refs: any = this.$refs;
    const actionOP = refs[`action-${index}`];
    if (actionOP) {
      actionOP.hide(event);
    }
    await this.onDownload(event, index, type, id);
    PrintReceiptController.activity({
      id: id,
      payload: new ActivityRequest(true)
    });
    this.fetchListInvoices();
  }

  goToEditReceipt(id: number) {
    this.$router.push(`/shipment/receipt-v2/edit/${id}`);
  }

  maskSensitiveData(contact: string): string {
    if (!contact) return "";

    // Mask phone: 087***2738
    if (contact.length <= 6) {
      return contact;
    }
    const start = contact.substring(0, 3);
    const end = contact.substring(contact.length - 4);
    return `${start}***${end}`;
  }

  async closeModal() {
    this.showModalUpdateInvoice = false;
    await this.fetchListInvoices();
  }
}

export default PrintReceiptV2;
